import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AppError } from '../../api/contract';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { approvalsRepositoryToken } from './repository';
import type { ApprovalDetail } from './types';

export interface ApprovalDecisionDialogProps {
  readonly approval: ApprovalDetail;
  readonly dataSource: DataSource;
  readonly decision: 'APPROVE' | 'REJECT' | null;
  readonly onClose: (completed: boolean) => void;
}

export function ApprovalDecisionDialog({
  approval,
  dataSource,
  decision,
  onClose,
}: ApprovalDecisionDialogProps) {
  const repository = useRepository(approvalsRepositoryToken);
  const { t } = useTranslation();
  const open = decision !== null;
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [decided, setDecided] = useState(false);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [error, setError] = useState<AppError | null>(null);
  const submittingRef = useRef(false);
  const idempotencyKeyRef = useRef(crypto.randomUUID());
  const previouslyOpen = useRef(open);
  const valid = reason.trim().length > 0 && reason.trim().length <= 2000;

  useEffect(() => {
    if (open && !previouslyOpen.current) reset();
    previouslyOpen.current = open;
    // Resetting only when the dialog transitions from closed to open (mirrors SensitiveActionDialog).
  }, [open]);

  const reset = () => {
    setReason('');
    setBusy(false);
    setDecided(false);
    setRequestId(null);
    setError(null);
    submittingRef.current = false;
    idempotencyKeyRef.current = crypto.randomUUID();
  };

  const close = () => {
    if (busy) return;
    const completed = decided;
    reset();
    onClose(completed);
  };

  const submit = async () => {
    if (decision === null || !valid || busy || submittingRef.current || decided) return;
    submittingRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const result = await repository.decide(
        approval.id,
        { decision, reason: reason.trim() },
        idempotencyKeyRef.current,
      );
      setDecided(true);
      setRequestId(result.requestId);
      idempotencyKeyRef.current = crypto.randomUUID();
    } catch (cause) {
      setError(safeError(cause, t('approvals.errors.decision')));
      submittingRef.current = false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      aria-describedby="approval-decision-description"
      fullWidth
      maxWidth="sm"
      onClose={close}
      open={open}
    >
      <DialogTitle>
        {decision === 'REJECT'
          ? t('approvals.decision.rejectTitle')
          : t('approvals.decision.approveTitle')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <DataSourceBadge source={dataSource} />
          <DialogContentText id="approval-decision-description">
            {approval.actionType} · {approval.entityType}
          </DialogContentText>
          {decided ? (
            <Alert severity="success">
              {t('approvals.decision.decided')}
              {requestId === null ? null : ` ${t('approvals.fields.requestId')}: ${requestId}`}
            </Alert>
          ) : (
            <TextField
              autoFocus
              disabled={busy}
              label={t('approvals.decision.reason')}
              minRows={2}
              multiline
              onChange={(event) => setReason(event.target.value)}
              slotProps={{ htmlInput: { maxLength: 2000, required: true } }}
              value={reason}
            />
          )}
          {error === null ? null : (
            <Alert severity="error">
              {error.message}
              {error.requestId === null ? null : ` Request ID: ${error.requestId}`}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={close}>
          {t('actions.close')}
        </Button>
        {decided ? null : (
          <Button
            disabled={!valid || busy}
            loading={busy}
            onClick={() => void submit()}
            variant="contained"
          >
            {t('approvals.decision.submit')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function safeError(cause: unknown, fallback: string): AppError {
  const error = isAppError(cause) ? cause : null;
  return {
    status: error?.status ?? null,
    code: error?.code ?? 'APPROVAL_DECISION_FAILED',
    message: fallback,
    requestId: error?.requestId ?? null,
    fieldErrors: {},
    retryAfterSeconds: error?.retryAfterSeconds ?? null,
  };
}

function isAppError(value: unknown): value is AppError {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Partial<AppError>).code === 'string' &&
    'requestId' in value
  );
}
